import React from 'react';
import DOMPurify from 'dompurify';
import { useForumsByCourses, type MoodleInstance } from '../../../../hooks/useMoodleHooks';
import type { MoodleForum } from '../../../../types/moodleTypes';

type Props = {
  courseId: number;
  onSelectForum: (forumId: number) => void;
  instance?: MoodleInstance;
};

export default function ForumsByCourse({ courseId, onSelectForum, instance = 'external' }: Props) {
  const { data, isLoading, error } = useForumsByCourses([courseId], undefined, instance);

  if (isLoading) {
    return (
      <div className="center" aria-busy="true">
        <progress aria-label="Loading forums" />
        <style>{`.center{display:flex;align-items:center;justify-content:center;padding:var(--hbr-spacing-lg);}`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className="error">
        {error.message}
        <style>{`.error{color:red;font-family:var(--hbr-font-body);}`}</style>
      </p>
    );
  }

  const forums: MoodleForum[] = data || [];

  if (!forums.length) {
    return <p style={{ fontFamily: 'var(--hbr-font-body)' }}>No forums available.</p>;
  }

  return (
    <ul className="forum-list">
      {forums.map((f) => (
        <li key={f.id} className="forum-item">
          <button type="button" className="forum-card" onClick={() => onSelectForum(f.id)}>
            <div className="forum-name">{f.name || f.shortname || `Forum ${f.id}`}</div>
            {f.intro && <div className="forum-intro" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(f.intro) }} />}
          </button>
        </li>
      ))}
      <style>
        {`
          .forum-list {
            list-style: none; margin: 0; padding: 0;
            display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: var(--hbr-spacing-md);
          }
          .forum-card {
            width: 100%; text-align: left; padding: var(--hbr-spacing-md);
            border-radius: 10px; border: 1px solid rgba(0,0,0,0.12); background: #fff; cursor: pointer;
            transition: box-shadow 0.2s ease, background 0.2s ease;
          }
          .forum-card:hover { background: rgba(10,102,255,0.04); box-shadow: 0 6px 16px rgba(0,0,0,0.08); }
          .forum-name { font-family: var(--hbr-font-heading); font-size: var(--hbr-font-size-lg); font-weight: var(--hbr-font-weight-semibold); margin-bottom: var(--hbr-spacing-2xs); }
          .forum-intro { font-family: var(--hbr-font-body); font-size: var(--hbr-font-size-sm); color: rgba(0,0,0,0.75); }
        `}
      </style>
    </ul>
  );
}